import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ProcessDraftEventNode, ProcessDraftStep, StepInputEvent } from '@bliks-io/contracts';
import { parseEventNodeId } from '@bliks-io/ui';
import { runDraftTransaction } from '@/api/mutations/helpers/runDraftTransaction';
import { mintTempDraftEventId } from '@/api/mutations/useProcessDraft/shared';
import { envelopePlaceholderReconcilers } from '@/api/mutations/helpers/placeholderStep';
import { addDraftEventApi } from '@/api/persist/draft';
import { useDeleteDraftEvent } from '@/api/mutations/useProcessDraft';
import { useDraftGesture } from '@/api/mutations/helpers/draftMutation';
import { upgradeLegacyEventInput } from '../edge-actions/shared';
import type { EdgeActionDeps } from '../edge-actions/types';

// NODE_WIDTH (180) + 60 — mirrors the end-node sentinel in `editorAdapter.ts`
// so step-attached events land directly left of the step at matching Y.
const EVENT_ATTACH_OFFSET_X = 240;

interface UseEventActionsArgs {
  processId: string;
  edgeActionDeps: EdgeActionDeps;
}

export interface EventActions {
  /**
   * Add a first-class draft event. When `attachToStepId` is provided the new
   * event is also wired to that step's event input.
   */
  addEvent: (position: { x: number; y: number }, attachToStepId?: string) => Promise<void>;
  /** Delete a draft event entity and strip referencing step inputs. */
  deleteEvent: (eventId: string) => Promise<void>;
  /**
   * Delete an event node by id: a legacy `event-<stepId>-<inputIdx>` node is
   * promoted to a first-class event server-side first.
   */
  deleteLegacyEvent: (nodeId: string) => Promise<void>;
}

export function useEventActions({ processId, edgeActionDeps }: UseEventActionsArgs): EventActions {
  const queryClient = useQueryClient();
  const deleteEventMutation = useDeleteDraftEvent(processId);
  const deleteEventAsync = deleteEventMutation.mutateAsync;

  // Add/delete-event skip pushing undo: re-creating an event mints a fresh
  // `_id`, which would break any attached step input references a redo would
  // need to restore. Users can re-add manually via the context menu.
  const addEvent = useDraftGesture(
    processId,
    async (position: { x: number; y: number }, attachToStepId?: string) => {
      const attachedStep = attachToStepId
        ? edgeActionDeps
            .getCachedSteps()
            .find((s) => s._id === attachToStepId || String(s.stableStepId) === attachToStepId)
        : undefined;
      const stepPos = attachedStep
        ? edgeActionDeps.getPositionsById().get(String(attachedStep.stableStepId))
        : undefined;
      const effectivePosition = stepPos
        ? { x: stepPos.x - EVENT_ATTACH_OFFSET_X, y: stepPos.y }
        : position;
      const tempEventId = mintTempDraftEventId();

      // Stage the event + its attach on the target step in one cache write so
      // the node doesn't render floating before the server answers.
      await runDraftTransaction({
        queryClient,
        processId,
        optimistic: (prev) => {
          // Canonical row shape: the editor consumes `response.events` rows
          // keyed by stableStepId (the event's public id) — the row's own
          // `_id` is storage identity, so the temp id stands in for both.
          const placeholderEvent: ProcessDraftEventNode = {
            _id: tempEventId,
            stableStepId: tempEventId,
            processId,
            draftId: String(prev.draft._id),
            nodeKind: 'event',
            eventKind: 'intermediate',
            eventType: 'none',
            name: '',
            description: ''
          };
          const events = [...(prev.events ?? []), placeholderEvent];
          const steps = attachedStep
            ? prev.steps.map((s) => {
                if (s._id !== attachedStep._id) return s;
                const existingEventInput = (s.inputs ?? []).findIndex(
                  (inp) => inp.inputType === 'event'
                );
                const newEventInput = {
                  inputType: 'event' as const,
                  type: 'none',
                  name: '',
                  description: '',
                  eventId: tempEventId
                } as unknown as StepInputEvent;
                const nextInputs =
                  existingEventInput >= 0
                    ? (s.inputs ?? []).map((inp, idx) =>
                        idx === existingEventInput ? newEventInput : inp
                      )
                    : [...(s.inputs ?? []), newEventInput];
                return { ...s, inputs: nextInputs } as ProcessDraftStep;
              })
            : prev.steps;
          return {
            ...prev,
            draft: { ...prev.draft, updatedAt: new Date() },
            events,
            steps
          };
        },
        // Node-id keyed (ADR 0003 P3b): event positions live in
        // stepPositions under the event's stable id, like any other node.
        optimisticLayout: (prev) => ({
          ...prev,
          stepPositions: [
            ...prev.stepPositions,
            {
              stableStepId: tempEventId,
              x: effectivePosition.x,
              y: effectivePosition.y
            }
          ]
        }),
        persist: () =>
          addDraftEventApi(processId, {
            position: effectivePosition,
            ...(attachedStep ? { attachToStepId: attachedStep._id } : {})
          }),
        ...envelopePlaceholderReconcilers(tempEventId)
      });
    }
  );

  const deleteEvent = useCallback(
    async (eventId: string) => {
      await deleteEventAsync(eventId);
    },
    [deleteEventAsync]
  );

  // Canonical ids pass `parseEventNodeId`; legacy `event-<stepId>-<idx>` ids
  // fall through and get upgraded server-side before we delete.
  const deleteLegacyEvent = useDraftGesture(processId, async (nodeId: string) => {
    const eventId =
      parseEventNodeId(nodeId) ?? (await upgradeLegacyEventInput(nodeId, edgeActionDeps));
    if (!eventId) return;
    await deleteEventAsync(eventId);
  });

  return { addEvent, deleteEvent, deleteLegacyEvent };
}
